'use client';

import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { api } from '@/lib/api';
import { formatPrice, formatVolume } from '@/lib/format';
import CoinLogo from './CoinLogo';
import { buildCoinRoute } from '@/lib/instrument';

interface LiqRanking {
  rank: number;
  symbol: string;
  totalLiquidation: number;
  longLiquidation: number;
  shortLiquidation: number;
  longPercentage: number;
  shortPercentage: number;
  liquidationCount: number;
}

interface LiqSummary {
  interval: string;
  totalLiquidation: number;
  longLiquidation: number;
  shortLiquidation: number;
  longPercentage: number;
  shortPercentage: number;
  liquidationCount: number;
}

interface LiqRecent {
  id: number;
  exchange: string;
  symbol: string;
  side: string;
  price: number;
  amount: number;
  value: number;
  time: string;
}

export default function LiquidationsView({ language }: { language: string }) {
  const [interval, setInterval] = useState<'1h' | '4h' | '24h'>('1h');
  const [showAllRecent, setShowAllRecent] = useState(false);
  const [mobileTab, setMobileTab] = useState<'ranking' | 'recent'>('ranking');

  const { data: summary } = useQuery({
    queryKey: ['liq-summary', interval],
    queryFn: () => api.get<LiqSummary>(`/liquidations/summary?interval=${interval}`),
    refetchInterval: 15_000,
  });

  const { data: ranking } = useQuery({
    queryKey: ['liq-ranking', interval],
    queryFn: () => api.get<LiqRanking[]>(`/liquidations/ranking?interval=${interval}&limit=50`),
    refetchInterval: 30_000,
  });

  const { data: recent } = useQuery({
    queryKey: ['liq-recent'],
    queryFn: () => api.get<LiqRecent[]>('/liquidations?limit=50'),
    refetchInterval: 5_000,
  });

  const periods: Array<{ key: '1h' | '4h' | '24h'; label: string }> = [
    { key: '1h', label: '1H' },
    { key: '4h', label: '4H' },
    { key: '24h', label: '24H' },
  ];

  const recentList = recent ?? [];
  const visibleRecent = showAllRecent ? recentList : recentList.slice(0, 5);

  return (
    <div className="space-y-4">
      <div className="flex gap-2">
        {periods.map((p) => (
          <button
            key={p.key}
            onClick={() => { setInterval(p.key); setShowAllRecent(false); }}
            className={`rounded-full border px-3 py-1 text-xs ${
              interval === p.key
                ? 'border-gf-accent bg-gf-accent/15 text-gf-accent'
                : 'border-gf-border bg-gf-surface2 text-gf-muted hover:text-gf-text'
            }`}
          >
            {p.label}
          </button>
        ))}
      </div>

      <div className="grid grid-cols-2 gap-2 sm:grid-cols-4 sm:gap-3">
        <div className="card !p-2.5 sm:!p-4">
          <div className="text-[10px] text-gf-muted sm:text-xs">{language !== 'en' ? '总爆仓' : 'Total'}</div>
          <div className="mt-0.5 text-base font-bold sm:mt-1 sm:text-lg liq-pulse">{summary ? formatVolume(summary.totalLiquidation) : '-'}</div>
        </div>
        <div className="card !p-2.5 sm:!p-4">
          <div className="text-[10px] text-gf-positive sm:text-xs">{language !== 'en' ? '多单爆仓' : 'Long Liq'}</div>
          <div className="mt-0.5 text-base font-bold text-gf-positive sm:mt-1 sm:text-lg">{summary ? formatVolume(summary.longLiquidation) : '-'}</div>
        </div>
        <div className="card !p-2.5 sm:!p-4">
          <div className="text-[10px] text-gf-negative sm:text-xs">{language !== 'en' ? '空单爆仓' : 'Short Liq'}</div>
          <div className="mt-0.5 text-base font-bold text-gf-negative sm:mt-1 sm:text-lg">{summary ? formatVolume(summary.shortLiquidation) : '-'}</div>
        </div>
        <div className="card !p-2.5 sm:!p-4">
          <div className="text-[10px] text-gf-muted sm:text-xs">{language !== 'en' ? '多空占比' : 'L/S Ratio'}</div>
          <div className="mt-0.5 text-base font-bold sm:mt-1 sm:text-lg">
            {summary ? `${summary.longPercentage.toFixed(1)}%/${summary.shortPercentage.toFixed(1)}%` : '-'}
          </div>
        </div>
      </div>

      {summary && (
        <div className="card">
          <div className="mb-2 flex h-3 overflow-hidden rounded-full bg-gf-surface2">
            <div className="bg-gf-positive transition-all duration-500" style={{ width: `${summary.longPercentage}%` }} />
            <div className="bg-gf-negative transition-all duration-500" style={{ width: `${summary.shortPercentage}%` }} />
          </div>
          <div className="flex justify-between text-xs">
            <span className="text-gf-positive">{language !== 'en' ? '多单' : 'Long'} {summary.longPercentage.toFixed(1)}%</span>
            <span className="text-gf-negative">{language !== 'en' ? '空单' : 'Short'} {summary.shortPercentage.toFixed(1)}%</span>
          </div>
        </div>
      )}

      {/* Mobile: ranking / recent tab switch */}
      <div className="flex gap-2 sm:hidden">
        <button
          type="button"
          onClick={() => setMobileTab('ranking')}
          className={`rounded-full border px-3 py-1 text-xs ${
            mobileTab === 'ranking'
              ? 'border-gf-accent bg-gf-accent/15 text-gf-accent'
              : 'border-gf-border bg-gf-surface2 text-gf-muted'
          }`}
        >
          {language !== 'en' ? '爆仓排行榜' : 'Ranking'}
        </button>
        {interval === '1h' && (
          <button
            type="button"
            onClick={() => setMobileTab('recent')}
            className={`rounded-full border px-3 py-1 text-xs ${
              mobileTab === 'recent'
                ? 'border-gf-accent bg-gf-accent/15 text-gf-accent'
                : 'border-gf-border bg-gf-surface2 text-gf-muted'
            }`}
          >
            {language !== 'en' ? '最近爆仓' : 'Recent'} <span className="text-gf-positive">●</span>
          </button>
        )}
      </div>

      <div className="grid gap-3 sm:grid-cols-3">
        {/* Recent liquidations - 1h only, desktop right 1/3, mobile tab */}
        {interval === '1h' && (
        <div className={`card !p-0 overflow-hidden ${mobileTab === 'recent' ? 'sm:col-span-3' : 'hidden sm:block sm:col-span-1'}`}>
          <div className="border-b border-gf-border px-4 py-2 text-xs font-medium text-gf-muted">
            {language !== 'en' ? '最近爆仓' : 'Recent Liquidations'}
            <span className="ml-2 text-gf-positive">● LIVE</span>
          </div>
          {recentList.length === 0 ? (
            <div className="p-8 text-center text-sm text-gf-muted">
              {language !== 'en' ? '数据采集中，请稍候...' : 'Collecting data, please wait...'}
            </div>
          ) : (
            <>
              <div className={showAllRecent ? 'max-h-[65vh] overflow-y-auto' : ''}>
                {visibleRecent.map((liq) => {
                  const base = liq.symbol.replace('USDT', '');
                  const isBig = liq.value >= 100_000;
                  return (
                    <div key={liq.id} className={`flex items-center gap-2 border-b border-gf-border/50 px-3 py-2 text-xs hover:bg-gf-surface2/30 sm:gap-3 sm:px-4 sm:py-2.5 ${
                      isBig ? 'bg-gf-negative/5' : ''
                    }`}>
                      <span className={`w-9 shrink-0 rounded px-1 py-0.5 text-center text-[9px] font-medium sm:w-10 sm:py-1 sm:text-[10px] ${
                        liq.side === 'long' ? 'bg-gf-positive/20 text-gf-positive' : 'bg-gf-negative/20 text-gf-negative'
                      }`}>
                        {liq.side === 'long' ? (language !== 'en' ? '多单' : 'Long') : (language !== 'en' ? '空单' : 'Short')}
                      </span>
                      <a href={buildCoinRoute({ symbol: liq.symbol, exchange: liq.exchange })} className="flex min-w-0 flex-1 items-center gap-1.5 hover:text-gf-accent sm:gap-2">
                        <CoinLogo symbol={liq.symbol} size={20} />
                        <div className="min-w-0 leading-tight">
                          <div className="flex items-center gap-1 whitespace-nowrap">
                            <span className="font-medium text-xs sm:text-sm">{base}</span>
                            <span className="hidden text-xs text-gf-muted sm:inline">/ USDT</span>
                            <span className="rounded bg-gf-accent/10 px-1 py-px text-[8px] text-gf-accent sm:text-[9px]">
                              {language !== 'en' ? '永续' : 'Perp'}
                            </span>
                            {isBig && (
                              <span className="rounded bg-gf-negative/20 px-1 py-px text-[8px] font-bold text-gf-negative sm:text-[9px]">
                                {language !== 'en' ? '大额' : 'BIG'}
                              </span>
                            )}
                          </div>
                          <div className="text-[9px] text-gf-muted sm:text-[10px]">{liq.exchange}</div>
                        </div>
                      </a>
                      <div className="shrink-0 text-right">
                        <div className={`font-mono text-xs font-medium sm:text-sm ${isBig ? 'text-gf-negative' : ''}`}>{formatVolume(liq.value)}</div>
                        <div className="hidden font-mono text-[10px] text-gf-muted sm:block">{liq.amount.toFixed(2)} {base}</div>
                      </div>
                      <div className="hidden w-20 shrink-0 text-right sm:block">
                        <div className="font-mono">{liq.price ? formatPrice(liq.price) : '—'}</div>
                        <div className="text-[10px] text-gf-muted">
                          {new Date(parseInt(liq.time)).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit', second: '2-digit' })}
                        </div>
                      </div>
                      <div className="w-14 shrink-0 text-right sm:hidden">
                        <div className="font-mono text-[10px] text-gf-muted">
                          {new Date(parseInt(liq.time)).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
              {recentList.length > 5 && (
                <div className="border-t border-gf-border px-4 py-2 text-center">
                  <button
                    onClick={() => setShowAllRecent(!showAllRecent)}
                    className="text-xs text-gf-accent hover:underline"
                  >
                    {showAllRecent
                      ? (language !== 'en' ? '收起' : 'Show Less')
                      : (language !== 'en' ? `查看全部 ${recentList.length} 条` : `Show All ${recentList.length}`)}
                  </button>
                </div>
              )}
            </>
          )}
        </div>
        )}

        {/* Ranking - desktop (left 2/3) */}
        <div className={`card !p-0 overflow-hidden hidden sm:block sm:col-span-2`}>
          <div className="border-b border-gf-border px-4 py-2 text-xs font-medium text-gf-muted">
            {language !== 'en' ? '爆仓排行榜' : 'Liquidation Ranking'}
            <span className="ml-2 text-[10px] text-gf-muted/70">{interval}</span>
          </div>
          <div className="max-h-[70vh] overflow-y-auto">
            <table className="table-base">
              <thead className="bg-gf-surface2/60 sticky top-0">
                <tr>
                  <th className="text-left">#</th>
                  <th className="text-left">{language !== 'en' ? '币种' : 'Coin'}</th>
                  <th className="text-right">{language !== 'en' ? '总爆仓' : 'Total'}</th>
                  <th className="text-right">{language !== 'en' ? '多单' : 'Long'}</th>
                  <th className="text-right">{language !== 'en' ? '空单' : 'Short'}</th>
                  <th className="text-right">{language !== 'en' ? '多空比' : 'L/S %'}</th>
                  <th className="text-right">{language !== 'en' ? '次数' : 'Count'}</th>
                </tr>
              </thead>
              <tbody>
                {(ranking ?? []).map((r) => (
                  <tr key={r.symbol}>
                    <td className="text-gf-muted">{r.rank}</td>
                    <td>
                      <a href={buildCoinRoute(r.symbol)} className="flex items-center gap-2 hover:text-gf-accent">
                        <CoinLogo symbol={r.symbol} size={24} />
                        <span className="font-medium">{r.symbol.replace('USDT', '')}/USDT</span>
                      </a>
                    </td>
                    <td className="text-right font-mono">{formatVolume(r.totalLiquidation)}</td>
                    <td className="text-right font-mono text-gf-positive">{formatVolume(r.longLiquidation)}</td>
                    <td className="text-right font-mono text-gf-negative">{formatVolume(r.shortLiquidation)}</td>
                    <td className="text-right">
                      <span className="text-gf-positive">{r.longPercentage.toFixed(1)}%</span>
                      <span className="text-gf-muted"> / </span>
                      <span className="text-gf-negative">{r.shortPercentage.toFixed(1)}%</span>
                    </td>
                    <td className="text-right text-gf-muted">{r.liquidationCount}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>

        {/* Ranking - mobile (tab) */}
        {mobileTab === 'ranking' && (
        <div className="sm:hidden max-h-[70vh] space-y-2 overflow-y-auto">
          {(ranking ?? []).map((r) => (
            <a key={r.symbol} href={buildCoinRoute(r.symbol)} className="card flex items-center justify-between p-3">
              <div className="flex items-center gap-2">
                <span className="w-5 text-xs text-gf-muted">{r.rank}</span>
                <CoinLogo symbol={r.symbol} size={24} />
                <div>
                  <div className="font-semibold text-sm">{r.symbol.replace('USDT', '')}/USDT</div>
                  <div className="text-[10px] text-gf-muted">{r.liquidationCount} {language !== 'en' ? '次' : 'tx'}</div>
                </div>
              </div>
              <div className="text-right">
                <div className="font-mono text-sm">{formatVolume(r.totalLiquidation)}</div>
                <div className="text-[10px]">
                  <span className="text-gf-positive">{r.longPercentage.toFixed(0)}%</span>
                  <span className="text-gf-muted">/</span>
                  <span className="text-gf-negative">{r.shortPercentage.toFixed(0)}%</span>
                </div>
              </div>
            </a>
          ))}
        </div>
        )}
      </div>

      <style jsx>{`
        .liq-pulse {
          animation: liq-pulse 2s ease-in-out infinite;
        }
        @keyframes liq-pulse {
          0%, 100% { opacity: 1; }
          50% { opacity: 0.85; }
        }
      `}</style>
    </div>
  );
}
